
import React, { useState, useMemo } from 'react';
import { AIEvent, Theme } from '../types';
import { PRIORITY_LEVELS } from '../constants';

interface EventTableProps {
  events: AIEvent[];
  onSelectEvent: (id: string) => void;
  onDeleteEvent: (event: AIEvent) => void;
  onManualCreate: () => void;
  deletingId?: string | null;
  isLoading?: boolean;
  fetchError?: string | null;
  theme: Theme;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

type SortOption = 'date_asc' | 'date_desc' | 'priority_high' | 'priority_low' | 'name_asc';

const EventTable: React.FC<EventTableProps> = ({ 
  events, 
  onSelectEvent, 
  onDeleteEvent,
  onManualCreate,
  deletingId,
  isLoading, 
  fetchError, 
  theme 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const [filterMonth, setFilterMonth] = useState('All');
  const [sortOption, setSortOption] = useState<SortOption>('date_asc');

  const categories = useMemo(() => ['All', ...Array.from(new Set(events.map(e => e.category).filter(Boolean))).sort()], [events]);
  
  const getPriorityWeight = (p: string) => {
    switch (p) {
      case 'High': return 3;
      case 'Medium': return 2;
      case 'Low': return 1;
      case 'Monitor Only': return 0;
      default: return -1;
    }
  };

  const processedEvents = useMemo(() => {
    // 1. Filter
    let result = events.filter(e => {
      const matchesSearch = e.campaignName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           e.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           e.focusArea.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'All' || e.category === filterCategory;
      const matchesPriority = filterPriority === 'All' || e.priority === filterPriority;
      const matchesMonth = filterMonth === 'All' || e.month === parseInt(filterMonth);
      
      return matchesSearch && matchesCategory && matchesPriority && matchesMonth;
    });

    // 2. Sort
    result.sort((a, b) => {
      switch (sortOption) {
        case 'date_asc':
          return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
        case 'date_desc':
          return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
        case 'priority_high':
          return getPriorityWeight(b.priority) - getPriorityWeight(a.priority);
        case 'priority_low':
          return getPriorityWeight(a.priority) - getPriorityWeight(b.priority);
        case 'name_asc':
          return a.campaignName.localeCompare(b.campaignName);
        default:
          return 0;
      }
    });

    return result;
  }, [events, searchTerm, filterCategory, filterPriority, filterMonth, sortOption]);

  const isLight = theme === 'light';

  const selectBaseStyle = `bg-transparent border ${isLight ? 'border-slate-200 text-slate-700' : 'border-white/10 text-white'} rounded-lg px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-accent/20 transition-all cursor-pointer appearance-none pr-8 relative`;

  const getPriorityBadge = (priority: string) => {
    const base = "px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border";
    switch (priority) {
      case 'High':
        return isLight 
          ? `${base} bg-red-50 border-red-100 text-red-600` 
          : `${base} bg-accent/20 border-accent/30 text-accent`;
      case 'Medium':
        return isLight
          ? `${base} bg-orange-50 border-orange-100 text-orange-600`
          : `${base} bg-white/10 border-white/20 text-white/80`;
      case 'Low':
        return isLight
          ? `${base} bg-blue-50 border-blue-100 text-blue-600`
          : `${base} bg-transparent border-white/10 text-white/60`;
      default:
        return isLight
          ? `${base} bg-slate-50 border-slate-200 text-slate-400`
          : `${base} bg-transparent border-white/5 text-muted`;
    }
  };

  const optionClassName = isLight ? 'bg-white text-slate-900' : 'bg-surface text-white';

  return (
    <div className="space-y-6 animate-in fade-in duration-700">
      {/* Search & Actions Bar - Elevated White Card in Daylight */}
      <div className={`p-4 rounded-2xl border transition-all duration-300 ${
        isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-surface/40 border-white/5'
      } flex flex-col xl:flex-row gap-4 justify-between items-stretch xl:items-center`}>
        
        <div className="flex flex-col gap-4 w-full xl:w-auto">
           {/* Top Row: Search */}
           <div className="relative w-full xl:w-96 group">
            <input 
              type="text" 
              placeholder="Search Intelligence Database..." 
              className={`block w-full pl-10 pr-4 py-2.5 rounded-xl border focus:outline-none focus:ring-2 focus:ring-accent/20 text-sm tracking-tight transition-all ${
                isLight ? 'bg-slate-50 border-slate-100 text-slate-900 placeholder:text-slate-400 focus:bg-white' : 'bg-charcoal border-white/5 text-white placeholder:text-white/10'
              }`} 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
            />
            <svg className={`absolute left-3.5 top-3 w-4 h-4 transition-colors ${isLight ? 'text-slate-400 group-focus-within:text-accent' : 'text-white/20'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Bottom Row: Filters & Sort */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Category Filter */}
            <div className="relative">
              <select 
                value={filterCategory} 
                onChange={(e) => setFilterCategory(e.target.value)} 
                className={selectBaseStyle}
              >
                <option value="All" className={optionClassName}>All Sectors</option>
                {categories.filter(c => c !== 'All').map(c => <option key={c} value={c} className={optionClassName}>{c}</option>)}
              </select>
              <svg className="absolute right-3 top-3 w-3 h-3 pointer-events-none text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
            </div>
            
            {/* Priority Filter */}
            <div className="relative">
              <select 
                value={filterPriority} 
                onChange={(e) => setFilterPriority(e.target.value)} 
                className={selectBaseStyle}
              >
                <option value="All" className={optionClassName}>All Priorities</option>
                {PRIORITY_LEVELS.map(p => <option key={p} value={p} className={optionClassName}>{p}</option>)}
              </select>
              <svg className="absolute right-3 top-3 w-3 h-3 pointer-events-none text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
            </div>

            {/* Month Filter */}
            <div className="relative">
              <select 
                value={filterMonth} 
                onChange={(e) => setFilterMonth(e.target.value)} 
                className={selectBaseStyle}
              >
                <option value="All" className={optionClassName}>All Months</option>
                {MONTHS.map((m, idx) => <option key={m} value={idx + 1} className={optionClassName}>{m}</option>)}
              </select>
              <svg className="absolute right-3 top-3 w-3 h-3 pointer-events-none text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
            </div>

            {/* Sort Divider */}
            <div className={`w-px h-6 mx-1 ${isLight ? 'bg-slate-200' : 'bg-white/10'}`}></div>

            {/* Sort Dropdown */}
            <div className="relative">
              <select 
                value={sortOption} 
                onChange={(e) => setSortOption(e.target.value as SortOption)} 
                className={`${selectBaseStyle} ${isLight ? 'bg-slate-100 text-slate-900 border-slate-200' : 'bg-white/5 border-white/10 text-accent'} pr-8`}
              >
                <option value="date_asc" className={optionClassName}>Date (Earliest)</option>
                <option value="date_desc" className={optionClassName}>Date (Latest)</option>
                <option value="priority_high" className={optionClassName}>Priority (High)</option>
                <option value="priority_low" className={optionClassName}>Priority (Low)</option>
                <option value="name_asc" className={optionClassName}>Name (A-Z)</option>
              </select>
              <svg className="absolute right-3 top-3 w-3 h-3 pointer-events-none text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
            </div>
          </div>
        </div>
        
        <button 
          onClick={onManualCreate}
          className="btn-primary text-[12px] whitespace-nowrap px-6 flex items-center justify-center self-end xl:self-center font-black tracking-tight"
        >
          + New
        </button>
      </div>

      {/* Main Table Grid - Premium Clean Surface in Daylight */}
      <div className={`overflow-hidden border rounded-2xl transition-all duration-300 ${
        isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-surface border-white/5 shadow-2xl'
      } ${isLoading ? 'opacity-50' : 'opacity-100'}`}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 dark:divide-white/5">
            <thead className={isLight ? 'bg-slate-50/50' : 'bg-white/[0.02]'}>
              <tr>
                <th 
                  className="px-6 py-5 text-left text-[13px] font-semibold text-slate-500 dark:text-muted uppercase tracking-[0.2em] cursor-pointer hover:text-accent transition-colors whitespace-nowrap"
                  onClick={() => setSortOption(sortOption === 'name_asc' ? 'date_asc' : 'name_asc')}
                >
                  EVENT NAME
                  {sortOption === 'name_asc' && <span className="ml-1 text-accent">↓</span>}
                </th>
                <th className="px-6 py-5 text-left text-[13px] font-semibold text-slate-500 dark:text-muted uppercase tracking-[0.2em] whitespace-nowrap">SECTOR</th>
                <th 
                  className="px-6 py-5 text-left text-[13px] font-semibold text-slate-500 dark:text-muted uppercase tracking-[0.2em] cursor-pointer hover:text-accent transition-colors whitespace-nowrap"
                  onClick={() => setSortOption(sortOption === 'priority_high' ? 'priority_low' : 'priority_high')}
                >
                  PRIORITY
                  {sortOption.includes('priority') && <span className="ml-1 text-accent">{sortOption === 'priority_high' ? '↓' : '↑'}</span>}
                </th>
                <th 
                  className="px-6 py-5 text-left text-[13px] font-semibold text-slate-500 dark:text-muted uppercase tracking-[0.2em] cursor-pointer hover:text-accent transition-colors whitespace-nowrap"
                  onClick={() => setSortOption(sortOption === 'date_asc' ? 'date_desc' : 'date_asc')}
                >
                  START DATE
                  {sortOption.includes('date') && <span className="ml-1 text-accent">{sortOption === 'date_asc' ? '↑' : '↓'}</span>}
                </th>
                <th className="px-6 py-5 text-left text-[13px] font-semibold text-slate-500 dark:text-muted uppercase tracking-[0.2em] whitespace-nowrap">LOCATION / AUDIENCE</th>
                <th className="pl-6 pr-16 py-5 text-right text-[13px] font-semibold text-slate-500 dark:text-muted uppercase tracking-[0.2em] whitespace-nowrap">ACTIONS</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isLight ? 'divide-slate-50' : 'divide-white/5'}`}>
              {processedEvents.map((event, idx) => (
                <tr 
                  key={event.id} 
                  className={`group transition-all duration-200 cursor-pointer ${
                    isLight ? 'hover:bg-slate-50/80' : 'hover:bg-white/[0.03]'
                  } ${idx % 2 === 0 && isLight ? 'bg-white' : isLight ? 'bg-slate-50/20' : ''} ${deletingId === event.id ? 'opacity-50' : ''}`} 
                  onClick={() => onSelectEvent(event.id)}
                >
                  <td className="px-6 py-6">
                    <div className="flex flex-col">
                      <span className={`text-sm font-semibold tracking-tight transition-colors ${
                        isLight ? 'text-slate-900 group-hover:text-accent' : 'text-white group-hover:text-accent'
                      }`}>
                        {event.campaignName}
                      </span>
                      <span className={`text-[11px] truncate max-w-xs mt-1 tracking-wide font-medium ${isLight ? 'text-slate-400' : 'text-white/20'}`}>
                        {event.focusArea}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-6">
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${isLight ? 'text-slate-500' : 'text-white/60'}`}>
                      {event.category}
                    </span>
                  </td>
                  <td className="px-6 py-6">
                    <span className={getPriorityBadge(event.priority)}>
                      {event.priority || 'None'}
                    </span>
                  </td>
                  <td className="px-6 py-6">
                    <span className={`text-[11px] font-bold font-tabular whitespace-nowrap uppercase tracking-widest ${isLight ? 'text-slate-800' : 'text-white/80'}`}>
                      {event.startDate}
                    </span>
                  </td>
                  <td className="px-6 py-6">
                    <div className="flex flex-col gap-1.5">
                      <span className={`text-[11px] font-medium whitespace-nowrap uppercase tracking-widest ${isLight ? 'text-slate-500' : 'text-muted'}`}>
                        {event.location}
                      </span>
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5 opacity-70">
                          <span className={`text-[9px] font-bold uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-white/40'}`}>
                            Size: {event.attendeeSize || '--'}
                          </span>
                        </div>
                        {event.targetAudience && (
                           <span className={`text-[9px] font-medium truncate max-w-[120px] ${isLight ? 'text-slate-400' : 'text-white/30'}`} title={event.targetAudience}>
                             Target: {event.targetAudience}
                           </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="pl-6 pr-16 py-6 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteEvent(event);
                        }}
                        className={`p-2 rounded-lg transition-all active:scale-90 opacity-0 group-hover:opacity-100 ${
                          isLight ? 'text-slate-300 hover:text-red-500 hover:bg-red-50' : 'text-muted hover:text-red-500 hover:bg-red-500/10'
                        }`}
                        title="Delete Intelligence Record"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {processedEvents.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={6} className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center gap-2 opacity-30">
                      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      <span className="text-xs font-bold uppercase tracking-widest">No matching records found</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EventTable;
